import React from 'react';
import { useOrderbookData } from '../../../hooks/useOrderbookData';
import { range } from '../../../lib/arrayHelpers';
import { Skeleton } from '../../Skeleton/Skeleton';
import { Truncate } from '../../Truncate/Truncate';
import type { OrderbookUiProps } from '../Orderbook';
import {
  OrderbookBody,
  OrderbookContainer,
  OrderbookHeader,
  OrderbookRowsSection,
} from '../OrderbookCommon';
import { OrderbookControls } from '../OrderbookControls';
import { OrderbookRows } from '../OrderbookRows';

export const SideBySideOrderbookUi: React.FC<OrderbookUiProps> = ({
  data,
  onRowClick,
  product,
  error,
  rowCount,
}) => {
  const { quoteTokenName } = product;

  const {
    asset,
    setAsset,
    grouping,
    setGrouping,
    asks,
    bids,
    priceDecimals,
    accountAskPrices,
    accountBidPrices,
    formatRowValue,
    getWidthAndDepth,
  } = useOrderbookData(data, product, rowCount);

  return (
    <OrderbookContainer>
      <OrderbookHeader>
        <span className="text-nowrap">
          Size (<Truncate text={asset} maxLength={6} />)
        </span>
        <span className="text-nowrap text-center">Price ({quoteTokenName})</span>
        <span className="text-nowrap text-right">
          Size (<Truncate text={asset} maxLength={6} />)
        </span>
      </OrderbookHeader>

      <OrderbookBody error={error}>
        <div className="flex min-h-0 flex-1 flex-row">
          {/* Bids */}
          <div className="flex flex-1 flex-col">
            <OrderbookRowsSection sideBySide={true}>
              {bids ? (
                <OrderbookRows
                  type="bid"
                  rows={bids}
                  rowCount={rowCount}
                  priceDecimals={priceDecimals}
                  accountPrices={accountBidPrices}
                  onRowClick={onRowClick}
                  formatValue={formatRowValue}
                  getWidthAndDepth={getWidthAndDepth}
                  showTotal={false}
                  columnOrder={['size', 'price']}
                />
              ) : (
                range(rowCount).map((i) => <Skeleton key={i} className="h-6" />)
              )}
            </OrderbookRowsSection>
          </div>

          {/* Asks */}
          <div className="flex flex-1 flex-col">
            <OrderbookRowsSection sideBySide={true} className="flex-col-reverse">
              {asks ? (
                <OrderbookRows
                  type="ask"
                  reversed={true}
                  rows={asks}
                  rowCount={rowCount}
                  priceDecimals={priceDecimals}
                  accountPrices={accountAskPrices}
                  onRowClick={onRowClick}
                  formatValue={formatRowValue}
                  getWidthAndDepth={getWidthAndDepth}
                  showTotal={false}
                  columnOrder={['size', 'price']}
                />
              ) : (
                range(rowCount).map((i) => <Skeleton key={i} className="h-6" />)
              )}
            </OrderbookRowsSection>
          </div>
        </div>
      </OrderbookBody>

      <OrderbookControls
        product={product}
        grouping={grouping}
        setGrouping={setGrouping}
        asset={asset}
        setAsset={setAsset}
      />
    </OrderbookContainer>
  );
};
